/**
 * PricingContext.js - Phase 57: Frontend Credit Enforcement
 * 
 * Global pricing context that fetches and caches pricing config
 * from /api/pricing on app load. Makes prices available to all
 * components without multiple API calls.
 * 
 * Also tracks user's unlocked assets to prevent showing unlock
 * modals for already-unlocked content.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API } from "@/App";

const PricingContext = createContext(null);

// Default prices (fallback if API fails)
const DEFAULT_PRICING = {
  scout_search: { cost: 1, name: "Research Search" },
  view_dossier: { cost: 3, name: "View Company Dossier" },
  export_pdf: { cost: 5, name: "Export PDF Report" },
  generate_slides: { cost: 15, name: "Generate Slide Deck" },
  generate_airpack: { cost: 50, name: "Deep Intelligence Pack" },
  airpack_asset: { cost: 20, name: "AIRPack Asset" },
  airpack_company: { cost: 50, name: "AIRPack Company" },
  company_dossier: { cost: 3, name: "Company Search" },
  brief: { cost: 3, name: "Deal Memo" }
};

export function PricingProvider({ children }) {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [unlockedAssets, setUnlockedAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  
  // Fetch pricing on mount
  useEffect(() => {
    fetchPricing();
  }, []);
  
  // Fetch user's unlocked assets when authenticated
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (token) {
      fetchUnlockedAssets();
    }
  }, []);
  
  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API}/pricing`);
      if (response.ok) {
        const data = await response.json();
        if (data.pricing) {
          setPricing(data.pricing);
          setLastFetched(Date.now());
        }
      }
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
      // Keep defaults
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUnlockedAssets = async () => {
    try {
      const token = localStorage.getItem("session_token");
      if (!token) return;
      
      const response = await fetch(`${API}/unlocks/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert to lookup map: { "dossier:MRNA": true, ... }
        const unlockMap = {};
        for (const unlock of (data.unlocks || [])) {
          const key = `${unlock.asset_type}:${unlock.asset_id}`;
          unlockMap[key] = unlock;
        }
        setUnlockedAssets(unlockMap);
      }
    } catch (error) {
      console.error("Failed to fetch unlocked assets:", error);
    }
  };
  
  // Get price for a specific action
  const getPrice = useCallback((actionKey) => {
    const config = pricing[actionKey];
    return config?.cost ?? DEFAULT_PRICING[actionKey]?.cost ?? 1;
  }, [pricing]);
  
  // Get full config for an action
  const getActionConfig = useCallback((actionKey) => {
    return pricing[actionKey] || DEFAULT_PRICING[actionKey] || {
      cost: 1,
      name: actionKey.replace(/_/g, " ")
    };
  }, [pricing]);
  
  // Check if an asset is unlocked
  const isUnlocked = useCallback((assetType, assetId) => {
    const key = `${assetType}:${assetId}`;
    return !!unlockedAssets[key];
  }, [unlockedAssets]);
  
  // Record an unlock (optimistically update local state)
  const recordUnlock = useCallback(async (assetType, assetId, creditsPaid) => {
    // Optimistic update
    const key = `${assetType}:${assetId}`;
    setUnlockedAssets(prev => ({
      ...prev,
      [key]: { asset_type: assetType, asset_id: assetId, credits_paid: creditsPaid }
    }));
    
    // API call
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/unlocks/record`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          asset_type: assetType,
          asset_id: assetId,
          credits_paid: creditsPaid
        })
      });
    } catch (error) {
      console.error("Failed to record unlock:", error);
      // Revert optimistic update on error
      setUnlockedAssets(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      throw error;
    }
  }, []);
  
  // Refresh pricing (can be called after admin updates)
  const refreshPricing = useCallback(async () => {
    await fetchPricing();
    await fetchUnlockedAssets();
  }, []);
  
  const value = {
    pricing,
    loading,
    lastFetched,
    getPrice,
    getActionConfig,
    isUnlocked,
    recordUnlock,
    refreshPricing,
    unlockedAssets
  };
  
  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing() {
  const context = useContext(PricingContext);
  if (!context) {
    // Return defaults if used outside provider
    return {
      pricing: DEFAULT_PRICING,
      loading: false,
      getPrice: (key) => DEFAULT_PRICING[key]?.cost ?? 1,
      getActionConfig: (key) => DEFAULT_PRICING[key] || { cost: 1, name: key },
      isUnlocked: () => false,
      recordUnlock: async () => {},
      refreshPricing: async () => {}
    };
  }
  return context;
}

export default PricingContext;
